import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAccounting } from "@/contexts/AccountingContext";
import { Search, Sparkles, Loader2, X, FileText, BookOpen, Package, ArrowUpDown, Clock, Receipt, Info, CheckCircle2, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Voucher, Ledger, StockItem } from "@/lib/accounting/types";
import { safeParseApiResponse } from "@/integrations/supabase/client";

interface SearchSummary {
  headline: string;
  badge?: string;
  badgeVariant?: "default" | "secondary" | "destructive" | "outline";
  highlightValue?: string;
  highlightLabel?: string;
  methodology?: string;
  dateRange?: string;
  kpis?: Array<{
    label: string;
    value: string;
    subtext?: string;
  }>;
}

interface SearchFilters {
  dataType: "vouchers" | "ledgers" | "stockItems" | "expenses" | "summary" | "mixed";
  filters: {
    voucherType?: string[];
    status?: string[];
    dateFrom?: string;
    dateTo?: string;
    partyName?: string;
    narration?: string;
    invoiceNumber?: string;
    minAmount?: number;
    maxAmount?: number;
    ledgerGroup?: string[];
    ledgerName?: string;
    balanceType?: "Dr" | "Cr";
    hasGST?: boolean;
    stockGroup?: string;
    lowStock?: boolean;
    paymentStatus?: string[];
  };
  displayTitle: string;
  summary?: SearchSummary;
  sortBy?: string;
  sortOrder?: string;
}

interface ExpenseResult {
  id: string;
  date: string;
  amount: number;
  category: string;
  vendor: string;
  description: string;
  paymentMethod: string;
  status: string;
}

interface ExtendedVoucher extends Voucher {
  paidAmount?: number;
  balanceDue?: number;
  paymentStatus?: string;
}

interface SearchResults {
  vouchers: ExtendedVoucher[];
  ledgers: Ledger[];
  stockItems: StockItem[];
  expenses: ExpenseResult[];
}

const EXAMPLE_QUERIES = [
  "Show unpaid invoices",
  "What are our total operating expenses excluding purchases?",
  "Show pending purchase vouchers",
  "What is our current net profit?",
  "Show low stock items",
  "What is our GST liability?",
  "Show all bank account balances",
  "Show invoices from last month",
  "Sales above ₹50,000",
];

function applyLocalFilters(
  vouchers: Voucher[],
  ledgers: Ledger[],
  stockItems: StockItem[],
  parsed: SearchFilters
): SearchResults {
  const f = parsed.filters;
  const results: SearchResults = { vouchers: [], ledgers: [], stockItems: [], expenses: [] };

  if (parsed.dataType === "vouchers" || parsed.dataType === "mixed") {
    let filtered: ExtendedVoucher[] = [...vouchers];
    if (f.voucherType?.length) filtered = filtered.filter(v => f.voucherType!.includes(v.type));
    if (f.status?.length) filtered = filtered.filter(v => f.status!.includes(v.status));
    if (f.dateFrom) filtered = filtered.filter(v => v.date >= f.dateFrom!);
    if (f.dateTo) filtered = filtered.filter(v => v.date <= f.dateTo!);
    if (f.partyName) filtered = filtered.filter(v => v.partyName?.toLowerCase().includes(f.partyName!.toLowerCase()));
    if (f.narration) filtered = filtered.filter(v => v.narration.toLowerCase().includes(f.narration!.toLowerCase()));
    if (f.invoiceNumber) filtered = filtered.filter(v => v.invoiceNumber?.toLowerCase().includes(f.invoiceNumber!.toLowerCase()));
    if (f.minAmount != null) filtered = filtered.filter(v => v.totalDebit >= f.minAmount!);
    if (f.maxAmount != null) filtered = filtered.filter(v => v.totalDebit <= f.maxAmount!);
    if (f.hasGST) filtered = filtered.filter(v => (v.gstAmount || 0) > 0);

    if (parsed.sortBy === "date") filtered.sort((a, b) => parsed.sortOrder === "asc" ? a.date.localeCompare(b.date) : b.date.localeCompare(a.date));
    else if (parsed.sortBy === "amount") filtered.sort((a, b) => parsed.sortOrder === "asc" ? a.totalDebit - b.totalDebit : b.totalDebit - a.totalDebit);

    results.vouchers = filtered;
  }

  if (parsed.dataType === "ledgers" || parsed.dataType === "mixed") {
    let filtered = [...ledgers];
    if (f.ledgerGroup?.length) filtered = filtered.filter(l => f.ledgerGroup!.some(g => l.group.toLowerCase().includes(g.toLowerCase())));
    if (f.ledgerName) filtered = filtered.filter(l => l.name.toLowerCase().includes(f.ledgerName!.toLowerCase()));
    if (f.balanceType) filtered = filtered.filter(l => l.currentBalanceType === f.balanceType);

    if (parsed.sortBy === "balance") filtered.sort((a, b) => parsed.sortOrder === "asc" ? a.currentBalance - b.currentBalance : b.currentBalance - a.currentBalance);
    else if (parsed.sortBy === "name") filtered.sort((a, b) => a.name.localeCompare(b.name));

    results.ledgers = filtered;
  }

  if (parsed.dataType === "stockItems" || parsed.dataType === "mixed") {
    let filtered = [...stockItems];
    if (f.stockGroup) filtered = filtered.filter(s => s.stockGroup.toLowerCase().includes(f.stockGroup!.toLowerCase()));
    if (f.lowStock) filtered = filtered.filter(s => s.reorderLevel && s.currentQty <= s.reorderLevel);

    results.stockItems = filtered;
  }

  return results;
}

export default function AISearchPage() {
  const { vouchers, ledgers, stockItems, activeCompany } = useAccounting();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [parsedFilter, setParsedFilter] = useState<SearchFilters | null>(null);
  const [results, setResults] = useState<SearchResults | null>(null);
  const [searchHistory, setSearchHistory] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem("accoupro_search_history");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const handleSearch = useCallback(async (searchQuery?: string) => {
    const q = (searchQuery ?? query).trim();
    if (!q) return;

    setLoading(true);
    try {
      const companyId = activeCompany?.id || localStorage.getItem("accoupro_active_company_id");
      
      const res = await fetch("/api/ai/search", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(companyId ? { "x-company-id": companyId } : {})
        },
        body: JSON.stringify({ query: q, companyId }),
      });

      const parsedRes = await safeParseApiResponse<any>(res);
      if (!parsedRes.ok || parsedRes.error) {
        throw parsedRes.error || new Error(`Server returned HTTP ${res.status}`);
      }

      const payload = parsedRes.data?.data || parsedRes.data;
      if (!payload) {
        throw new Error("Empty response received from search engine.");
      }

      const filterMeta: SearchFilters = {
        dataType: payload.dataType || "mixed",
        displayTitle: payload.displayTitle || `Results for "${q}"`,
        summary: payload.summary,
        filters: payload.filters || {},
        sortBy: payload.sortBy,
        sortOrder: payload.sortOrder
      };
      setParsedFilter(filterMeta);

      if (payload.results) {
        setResults({
          vouchers: payload.results.vouchers || [],
          ledgers: payload.results.ledgers || [],
          stockItems: payload.results.stockItems || [],
          expenses: payload.results.expenses || []
        });
      } else {
        const localFiltered = applyLocalFilters(vouchers, ledgers, stockItems, filterMeta);
        setResults(localFiltered);
      }

      setSearchHistory(prev => {
        const next = [q, ...prev.filter(h => h !== q)].slice(0, 8);
        try { localStorage.setItem("accoupro_search_history", JSON.stringify(next)); } catch {}
        return next;
      });
    } catch (err: any) {
      console.error("[AI Smart Search Error]:", err);
      toast.error(err.message || "Search failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [query, activeCompany, vouchers, ledgers, stockItems]);

  const totalResults = results
    ? (results.vouchers?.length || 0) +
      (results.ledgers?.length || 0) +
      (results.stockItems?.length || 0) +
      (results.expenses?.length || 0)
    : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold font-display text-foreground">AI Smart Search</h1>
        <p className="text-sm text-muted-foreground">Ask anything about your company&apos;s financial records in plain English</p>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <div className="flex items-center gap-2 p-2 rounded-xl bg-card border-2 border-accent/30 shadow-lg focus-within:border-accent transition-colors">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-accent/10">
            <Sparkles className="h-5 w-5 text-accent" />
          </div>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Try: Show unpaid invoices, total operating expenses, GST liability..."
            className="border-0 bg-transparent text-base focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground/50"
            disabled={loading}
          />
          {query && (
            <button
              onClick={() => { setQuery(""); setParsedFilter(null); setResults(null); }}
              className="p-2 text-muted-foreground hover:text-foreground"
              title="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={() => handleSearch()}
            disabled={loading || !query.trim()}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-accent text-accent-foreground text-sm font-medium hover:bg-accent/90 disabled:opacity-50 transition-colors"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Search
          </button>
        </div>
      </div>

      {/* Example Queries */}
      {!results && (
        <div className="space-y-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Suggested accounting questions</p>
          <div className="flex flex-wrap gap-2">
            {EXAMPLE_QUERIES.map((eq) => (
              <button
                key={eq}
                onClick={() => { setQuery(eq); handleSearch(eq); }}
                className="px-3 py-1.5 rounded-full bg-muted/60 border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors text-left"
              >
                {eq}
              </button>
            ))}
          </div>

          {searchHistory.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                <Clock className="h-3 w-3" /> Recent searches
              </p>
              <div className="flex flex-wrap gap-2">
                {searchHistory.map((h, i) => (
                  <button
                    key={i}
                    onClick={() => { setQuery(h); handleSearch(h); }}
                    className="px-3 py-1.5 rounded-full bg-accent/5 border border-accent/20 text-xs text-accent hover:bg-accent/10 transition-colors"
                  >
                    {h}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Results */}
      <AnimatePresence mode="wait">
        {results && parsedFilter && (
          <motion.div
            key="results"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            {/* Header & Calculation Summary Card */}
            <div className="p-5 rounded-xl bg-card border border-border shadow-sm space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border/50 pb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-semibold text-foreground">{parsedFilter.displayTitle}</h3>
                    {parsedFilter.summary?.badge && (
                      <Badge variant={parsedFilter.summary.badgeVariant || "outline"} className="text-xs">
                        {parsedFilter.summary.badge}
                      </Badge>
                    )}
                  </div>
                  {parsedFilter.summary?.headline && (
                    <p className="text-sm font-medium text-accent mt-0.5">{parsedFilter.summary.headline}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px] uppercase">{parsedFilter.dataType}</Badge>
                  {parsedFilter.summary?.dateRange && (
                    <Badge variant="secondary" className="text-[10px] flex items-center gap-1">
                      <Clock className="h-2.5 w-2.5" />
                      {parsedFilter.summary.dateRange}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Highlight KPI Callout */}
              {parsedFilter.summary?.highlightValue && (
                <div className="flex items-baseline gap-3 p-3 rounded-lg bg-accent/5 border border-accent/20">
                  <span className="text-2xl font-bold font-mono text-accent">
                    {parsedFilter.summary.highlightValue}
                  </span>
                  {parsedFilter.summary.highlightLabel && (
                    <span className="text-xs font-medium text-muted-foreground">
                      {parsedFilter.summary.highlightLabel}
                    </span>
                  )}
                </div>
              )}

              {/* KPI Grid */}
              {parsedFilter.summary?.kpis && parsedFilter.summary.kpis.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {parsedFilter.summary.kpis.map((kpi, idx) => (
                    <div key={idx} className="p-3 rounded-lg bg-muted/40 border border-border/60">
                      <p className="text-[11px] font-medium text-muted-foreground truncate">{kpi.label}</p>
                      <p className="text-sm font-bold text-foreground font-mono mt-0.5">{kpi.value}</p>
                      {kpi.subtext && <p className="text-[10px] text-muted-foreground mt-0.5">{kpi.subtext}</p>}
                    </div>
                  ))}
                </div>
              )}

              {/* Calculation Methodology Explanation */}
              {parsedFilter.summary?.methodology && (
                <div className="flex items-start gap-2.5 p-3 rounded-lg bg-muted/20 border border-border/40 text-xs text-muted-foreground">
                  <Info className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-foreground">Calculation Methodology: </span>
                    {parsedFilter.summary.methodology}
                  </div>
                </div>
              )}

              {/* Applied Filters Tags */}
              {Object.keys(parsedFilter.filters || {}).length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 pt-1">
                  <span className="text-[11px] text-muted-foreground">Applied filters:</span>
                  {Object.entries(parsedFilter.filters).map(([key, val]) => {
                    if (val == null || (Array.isArray(val) && val.length === 0)) return null;
                    return (
                      <Badge key={key} variant="secondary" className="text-[10px] font-normal">
                        {key}: {Array.isArray(val) ? val.join(", ") : String(val)}
                      </Badge>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Voucher Results */}
            {results.vouchers && results.vouchers.length > 0 && (
              <div className="p-4 rounded-xl bg-card border border-border shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-accent" />
                    <h4 className="text-sm font-semibold text-foreground">Vouchers ({results.vouchers.length})</h4>
                  </div>
                  <span className="text-xs text-muted-foreground">Click a row to view voucher details</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 text-xs text-muted-foreground font-medium">Voucher #</th>
                        <th className="text-left py-2 text-xs text-muted-foreground font-medium">Type</th>
                        <th className="text-left py-2 text-xs text-muted-foreground font-medium">Date</th>
                        <th className="text-left py-2 text-xs text-muted-foreground font-medium">Party</th>
                        <th className="text-right py-2 text-xs text-muted-foreground font-medium">Total Amount</th>
                        <th className="text-right py-2 text-xs text-muted-foreground font-medium">Paid</th>
                        <th className="text-right py-2 text-xs text-muted-foreground font-medium">Balance Due</th>
                        <th className="text-center py-2 text-xs text-muted-foreground font-medium">Payment</th>
                        <th className="text-center py-2 text-xs text-muted-foreground font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.vouchers.slice(0, 30).map((v) => {
                        const due = v.balanceDue ?? Math.max(0, (v.totalDebit || 0) - (v.paidAmount || 0));
                        return (
                          <tr key={v.id} className="border-b border-border/30 hover:bg-muted/30 transition-colors">
                            <td className="py-2.5 font-mono text-accent font-medium">{v.voucherNumber}</td>
                            <td className="py-2.5">{v.type}</td>
                            <td className="py-2.5 text-muted-foreground font-mono text-xs">{v.date}</td>
                            <td className="py-2.5 font-medium">{v.partyName || "—"}</td>
                            <td className="py-2.5 text-right font-mono font-semibold">₹{(v.totalDebit || 0).toLocaleString("en-IN")}</td>
                            <td className="py-2.5 text-right font-mono text-muted-foreground">₹{(v.paidAmount || 0).toLocaleString("en-IN")}</td>
                            <td className={`py-2.5 text-right font-mono font-bold ${due > 0 ? "text-destructive" : "text-success"}`}>
                              ₹{due.toLocaleString("en-IN")}
                            </td>
                            <td className="py-2.5 text-center">
                              <Badge
                                variant={v.paymentStatus === "Paid" ? "default" : v.paymentStatus === "Partially Paid" ? "secondary" : "destructive"}
                                className="text-[10px]"
                              >
                                {v.paymentStatus || (due === 0 ? "Paid" : "Unpaid")}
                              </Badge>
                            </td>
                            <td className="py-2.5 text-center">
                              <Badge
                                variant={v.status === "Approved" ? "default" : v.status === "Pending" ? "secondary" : "destructive"}
                                className="text-[10px]"
                              >
                                {v.status}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {results.vouchers.length > 30 && (
                    <p className="text-xs text-muted-foreground mt-2 text-center">Showing top 30 of {results.vouchers.length} vouchers</p>
                  )}
                </div>
              </div>
            )}

            {/* Expenses Results */}
            {results.expenses && results.expenses.length > 0 && (
              <div className="p-4 rounded-xl bg-card border border-border shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <Receipt className="h-4 w-4 text-accent" />
                  <h4 className="text-sm font-semibold text-foreground">Operating Expenses ({results.expenses.length})</h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 text-xs text-muted-foreground font-medium">Date</th>
                        <th className="text-left py-2 text-xs text-muted-foreground font-medium">Category</th>
                        <th className="text-left py-2 text-xs text-muted-foreground font-medium">Vendor</th>
                        <th className="text-left py-2 text-xs text-muted-foreground font-medium">Description</th>
                        <th className="text-right py-2 text-xs text-muted-foreground font-medium">Amount</th>
                        <th className="text-left py-2 text-xs text-muted-foreground font-medium">Payment Mode</th>
                        <th className="text-center py-2 text-xs text-muted-foreground font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.expenses.map((e) => (
                        <tr key={e.id} className="border-b border-border/30 hover:bg-muted/30 transition-colors">
                          <td className="py-2.5 text-muted-foreground font-mono text-xs">{e.date}</td>
                          <td className="py-2.5">
                            <Badge variant="outline" className="text-[10px]">{e.category}</Badge>
                          </td>
                          <td className="py-2.5 font-medium">{e.vendor || "—"}</td>
                          <td className="py-2.5 text-muted-foreground">{e.description || "—"}</td>
                          <td className="py-2.5 text-right font-mono font-bold text-foreground">₹{Number(e.amount).toLocaleString("en-IN")}</td>
                          <td className="py-2.5 text-xs text-muted-foreground">{e.paymentMethod || "—"}</td>
                          <td className="py-2.5 text-center">
                            <Badge variant={e.status === "Approved" ? "default" : "secondary"} className="text-[10px]">
                              {e.status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Ledger Results */}
            {results.ledgers && results.ledgers.length > 0 && (
              <div className="p-4 rounded-xl bg-card border border-border shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <BookOpen className="h-4 w-4 text-accent" />
                  <h4 className="text-sm font-semibold text-foreground">Ledgers ({results.ledgers.length})</h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 text-xs text-muted-foreground font-medium">Account Name</th>
                        <th className="text-left py-2 text-xs text-muted-foreground font-medium">Group</th>
                        <th className="text-right py-2 text-xs text-muted-foreground font-medium">Closing Balance</th>
                        <th className="text-center py-2 text-xs text-muted-foreground font-medium">Balance Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.ledgers.map((l) => (
                        <tr key={l.id} className="border-b border-border/30 hover:bg-muted/30 transition-colors">
                          <td className="py-2.5 font-medium text-foreground">{l.name}</td>
                          <td className="py-2.5 text-muted-foreground text-xs">{l.group}</td>
                          <td className="py-2.5 text-right font-mono font-bold text-foreground">₹{(l.currentBalance || 0).toLocaleString("en-IN")}</td>
                          <td className="py-2.5 text-center">
                            <Badge variant="outline" className="text-[10px] font-mono">{l.currentBalanceType}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Stock Results */}
            {results.stockItems && results.stockItems.length > 0 && (
              <div className="p-4 rounded-xl bg-card border border-border shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <Package className="h-4 w-4 text-accent" />
                  <h4 className="text-sm font-semibold text-foreground">Stock Items ({results.stockItems.length})</h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 text-xs text-muted-foreground font-medium">Item Name</th>
                        <th className="text-left py-2 text-xs text-muted-foreground font-medium">Stock Group</th>
                        <th className="text-right py-2 text-xs text-muted-foreground font-medium">Current Stock</th>
                        <th className="text-right py-2 text-xs text-muted-foreground font-medium">Reorder Level</th>
                        <th className="text-right py-2 text-xs text-muted-foreground font-medium">Rate</th>
                        <th className="text-center py-2 text-xs text-muted-foreground font-medium">Stock Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.stockItems.map((s) => {
                        const isLow = s.reorderLevel != null && s.currentQty <= s.reorderLevel;
                        return (
                          <tr key={s.id} className="border-b border-border/30 hover:bg-muted/30 transition-colors">
                            <td className="py-2.5 font-medium text-foreground">{s.name}</td>
                            <td className="py-2.5 text-muted-foreground text-xs">{s.stockGroup}</td>
                            <td className="py-2.5 text-right font-mono font-bold">{s.currentQty} {s.unit || "pcs"}</td>
                            <td className="py-2.5 text-right font-mono text-muted-foreground">{s.reorderLevel != null ? `${s.reorderLevel} ${s.unit || "pcs"}` : "—"}</td>
                            <td className="py-2.5 text-right font-mono font-semibold">₹{(s.currentRate || 0).toLocaleString("en-IN")}</td>
                            <td className="py-2.5 text-center">
                              {isLow ? (
                                <Badge variant="destructive" className="text-[10px] gap-1">
                                  <AlertTriangle className="h-2.5 w-2.5" /> Reorder Alert
                                </Badge>
                              ) : (
                                <Badge variant="default" className="text-[10px] gap-1">
                                  <CheckCircle2 className="h-2.5 w-2.5" /> Normal
                                </Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Empty Results state */}
            {totalResults === 0 && (
              <div className="text-center py-10 p-6 rounded-xl bg-card border border-border">
                <Search className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                <h4 className="text-sm font-semibold text-foreground">
                  {parsedFilter.summary?.headline || "No matching records found"}
                </h4>
                <p className="text-xs text-muted-foreground max-w-md mx-auto mt-1">
                  {parsedFilter.summary?.methodology || "Try rephrasing your search or using one of the suggested accounting queries above."}
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
